import { Box, Grid, Typography } from '@material-ui/core';
import { Button, Card, Tabs, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useGeneralTasksStore } from '../../hooks/useGeneralTasksStore';
import { DateFormat, formatDate, isNotBlank } from '@roc/feature-utils';
import { useUserStore } from '@roc/feature-app-core';
import { TaskV2 } from '@roc/feature-general-tasks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardWithTextArea: {
      '& .MuiCardHeader-root': {
        padding: 0,
      },
      '& .MuiCardContent-root': {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        height: '330px',
        overflow: 'auto'
      },
    },
    cardWithoutTextArea: {
      '& .MuiCardHeader-root': {
        padding: 0,
      },
      '& .MuiCardContent-root': {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        maxHeight: '60vh',
        overflow: 'auto'
      },
    },
    uwDetailsLabel: {
      fontSize: '14px',
      color: '#555',
    },
    uwCommentWrapper: {
      backgroundColor: '#F7F7F7',
      padding: theme.spacing(2),
      borderBottom: '1px solid #e1e1e1',
      width: '100%',
      margin: '10px 0'
    },
    uwCommentText: {
      fontSize: '1.08rem'
    },
  }),
);


interface UwCommentsProps {
  taskId: number;
}

export const UwComments = observer(({ taskId }: UwCommentsProps) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;
  const { taskV2 } = editTaskStore;
  const { userStore } = useUserStore();
  const [description, setDescription] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const classes = useStyles();

  useEffect(() => {
    setDescription('');
  }, [taskId]);

  const handleOnChange = (e) => {
    setErrorText('');
    setDescription(e.target.value);
  }

  const handleSavedSuccessfully = () => {
    setDescription('');
  }

  const handleSaveComment = () => {
    if (!description.length) {
      editTaskStore.showWarning('Please enter a comment.');
    } else {
      editTaskStore.addTaskNote(taskId, description, handleSavedSuccessfully);
    }
  }

  const getTitleName = (taskV2: TaskV2) => {
    const taskTitle = taskV2.title;
    const index = taskTitle.indexOf('-');

    return index >= 0  && index < taskTitle.length ?
      taskTitle.substring(index + 1) :
      taskTitle;
  }

  const allowComments = userStore.isUnderwriter || userStore.isCommittee;

  return (
    <Box display="flex" flexDirection="column" height="65vh" style={{ gap: '15px' }}>
      <Grid item xs={12}>
        <Card className={allowComments ? classes.cardWithTextArea : classes.cardWithoutTextArea} title={null}>
          {taskV2.taskNotes && taskV2.taskNotes.length > 0 ? (
            <>
              {taskV2.taskNotes.map((item, index) =>
                <Box key={`${taskV2.title}-${index}`} className={classes.uwCommentWrapper}>
                  <Typography className={classes.uwDetailsLabel}>{getTitleName(taskV2)}</Typography>
                  <Typography className={classes.uwCommentText}>{item.comment}</Typography>
                  <Typography className={classes.uwDetailsLabel}>{`${item.commentByUsername} | ${formatDate(item.commentDate, DateFormat.MMDDYYYY)}`}</Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box className={classes.uwCommentWrapper}>
                <Typography className={classes.uwCommentText}>There are no notes for this task.</Typography>
              </Box>
            </>
          )}
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {allowComments?
            <>
              <Grid item xs={12}>
                <TextField
                  type={'text'}
                  testId={'assignToOwner-description'}
                  variant={'outlined'}
                  label={'Comment:'}
                  value={description}
                  onChange={handleOnChange}
                  fullWidth
                  multiline={true}
                  minRows={4}
                  maxRows={6}
                  error={isNotBlank(errorText)}
                  helperText={errorText}
                />
              </Grid>
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleSaveComment}
                  color="primary"
                  variant="contained"
                  testId="confirm"
                >
                  Save
                </Button>
              </Grid>
            </> : <></>
          }
        </Grid>
      </Grid>
    </Box >
  )
});
